import type { CrmTranslate } from './crm-copy';

export function createCrmDealsCopy(t: CrmTranslate) {
  return {
    dealForm: {
      titles: {
        create: t('crm.dealForm.titles.create'),
        edit: t('crm.dealForm.titles.edit'),
      },
      actions: {
        cancel: t('crm.dealForm.actions.cancel'),
        create: t('crm.dealForm.actions.create'),
        update: t('crm.dealForm.actions.update'),
        creating: t('crm.dealForm.actions.creating'),
        updating: t('crm.dealForm.actions.updating'),
      },
      sections: {
        basicInformation: t('crm.dealForm.sections.basicInformation'),
        contactInformation: t('crm.dealForm.sections.contactInformation'),
      },
      labels: {
        dealNameRequired: t('crm.dealForm.labels.dealNameRequired'),
        dealAmountRequired: t('crm.dealForm.labels.dealAmountRequired'),
        stageRequired: t('crm.dealForm.labels.stageRequired'),
        company: t('crm.dealForm.labels.company'),
        probability: t('crm.dealForm.labels.probability'),
        expectedCloseDate: t('crm.dealForm.labels.expectedCloseDate'),
        contact: t('crm.dealForm.labels.contact'),
        description: t('crm.dealForm.labels.description'),
      },
      placeholders: {
        dealName: t('crm.dealForm.placeholders.dealName'),
        companySearch: t('crm.dealForm.placeholders.companySearch'),
        contactSearch: t('crm.dealForm.placeholders.contactSearch'),
        description: t('crm.dealForm.placeholders.description'),
      },
      emptyOptions: {
        selectStage: t('crm.dealForm.emptyOptions.selectStage'),
      },
      entitySelector: {
        createCompany: t('crm.dealForm.entitySelector.createCompany'),
        noCompaniesFound: t('crm.dealForm.entitySelector.noCompaniesFound'),
        createContact: t('crm.dealForm.entitySelector.createContact'),
        noContactsFound: t('crm.dealForm.entitySelector.noContactsFound'),
      },
      fallbacks: {
        unknownContact: t('crm.dealForm.fallbacks.unknownContact'),
        unnamedContact: t('crm.dealForm.fallbacks.unnamedContact'),
        contactWithId: (contactId: string) =>
          t('crm.dealForm.fallbacks.contactWithId', {
            values: { contactId },
          }),
        companyWithId: (companyId: string) =>
          t('crm.dealForm.fallbacks.companyWithId', {
            values: { companyId },
          }),
      },
      errors: {
        dealNameRequired: t('crm.dealForm.errors.dealNameRequired'),
        amountGreaterThanZero: t('crm.dealForm.errors.amountGreaterThanZero'),
        stageRequired: t('crm.dealForm.errors.stageRequired'),
        probabilityRange: t('crm.dealForm.errors.probabilityRange'),
      },
    },
    dealsPage: {
      documentTitle: t('crm.dealsPage.documentTitle'),
      title: t('crm.dealsPage.title'),
      counts: {
        deals: (count: number) =>
          t('crm.dealsPage.counts.deals', {
            values: { count },
          }),
      },
      actions: {
        grid: t('crm.dealsPage.actions.grid'),
        list: t('crm.dealsPage.actions.list'),
        importCsv: t('crm.dealsPage.actions.importCsv'),
        addDeal: t('crm.dealsPage.actions.addDeal'),
        clearSearch: t('crm.dealsPage.actions.clearSearch'),
        selectAllDealsTitle: t('crm.dealsPage.actions.selectAllDealsTitle'),
        unselectAllDealsTitle: t('crm.dealsPage.actions.unselectAllDealsTitle'),
        selectAll: t('crm.dealsPage.actions.selectAll'),
        unselectAll: t('crm.dealsPage.actions.unselectAll'),
        advancedFilters: t('crm.dealsPage.actions.advancedFilters'),
        removeSearchFilter: t('crm.dealsPage.actions.removeSearchFilter'),
        removeAdvancedFilter: (label: string) =>
          t('crm.dealsPage.actions.removeAdvancedFilter', {
            values: { label },
          }),
        clearAllFilters: t('crm.dealsPage.actions.clearAllFilters'),
        confirm: t('crm.dealsPage.actions.confirm'),
        cancel: t('crm.dealsPage.actions.cancel'),
      },
      search: {
        placeholder: t('crm.dealsPage.search.placeholder'),
      },
      sort: {
        label: t('crm.dealsPage.sort.label'),
        title: t('crm.dealsPage.sort.title'),
        amount: t('crm.dealsPage.sort.amount'),
        stage: t('crm.dealsPage.sort.stage'),
        created: t('crm.dealsPage.sort.created'),
        ascending: t('crm.dealsPage.sort.ascending'),
        descending: t('crm.dealsPage.sort.descending'),
        sortOrderTitle: (order: string) =>
          t('crm.dealsPage.sort.sortOrderTitle', {
            values: { order },
          }),
      },
      pageSize: {
        show: t('crm.dealsPage.pageSize.show'),
        perPage: t('crm.dealsPage.pageSize.perPage'),
      },
      filters: {
        activeFilters: t('crm.dealsPage.filters.activeFilters'),
        searchTag: (query: string) =>
          t('crm.dealsPage.filters.searchTag', {
            values: { query },
          }),
        advancedTag: (label: string, value: string) =>
          t('crm.dealsPage.filters.advancedTag', {
            values: { label, value },
          }),
      },
      advancedFilterLabels: {
        stage_id: t('crm.dealsPage.advancedFilterLabels.stageId'),
        contact_id: t('crm.dealsPage.advancedFilterLabels.contactId'),
        company_id: t('crm.dealsPage.advancedFilterLabels.companyId'),
        owner_id: t('crm.dealsPage.advancedFilterLabels.ownerId'),
        amount_min: t('crm.dealsPage.advancedFilterLabels.amountMin'),
        amount_max: t('crm.dealsPage.advancedFilterLabels.amountMax'),
        probability_min: t('crm.dealsPage.advancedFilterLabels.probabilityMin'),
        probability_max: t('crm.dealsPage.advancedFilterLabels.probabilityMax'),
        created_after: t('crm.dealsPage.advancedFilterLabels.createdAfter'),
        created_before: t('crm.dealsPage.advancedFilterLabels.createdBefore'),
        updated_after: t('crm.dealsPage.advancedFilterLabels.updatedAfter'),
        updated_before: t('crm.dealsPage.advancedFilterLabels.updatedBefore'),
        tags: t('crm.dealsPage.advancedFilterLabels.tags'),
      },
      formatters: {
        currencyAmount: (amount: string) =>
          t('crm.dealsPage.formatters.currencyAmount', {
            values: { amount },
          }),
        probability: (value: string) =>
          t('crm.dealsPage.formatters.probability', {
            values: { value },
          }),
      },
      states: {
        errorTitle: t('crm.dealsPage.states.errorTitle'),
        errorDescription: t('crm.dealsPage.states.errorDescription'),
        emptyTitle: t('crm.dealsPage.states.emptyTitle'),
        searchEmptyDescription: t('crm.dealsPage.states.searchEmptyDescription'),
        startEmptyDescription: t('crm.dealsPage.states.startEmptyDescription'),
      },
    },
    dealsList: {
      nouns: {
        deal: t('crm.dealsList.nouns.deal'),
        deals: t('crm.dealsList.nouns.deals'),
      },
      counts: {
        selected: (selected: number, total: number) =>
          t('crm.dealsList.counts.selected', {
            values: { selected, total },
          }),
        showing: (visible: number, total: number) =>
          t('crm.dealsList.counts.showing', {
            values: { visible, total },
          }),
      },
      actions: {
        confirm: t('crm.dealsList.actions.confirm'),
        cancel: t('crm.dealsList.actions.cancel'),
        delete: t('crm.dealsList.actions.delete'),
        deleting: t('crm.dealsList.actions.deleting'),
        edit: t('crm.dealsList.actions.edit'),
        undo: t('crm.dealsList.actions.undo'),
        selectAll: t('crm.dealsList.actions.selectAll'),
        unselectAll: t('crm.dealsList.actions.unselectAll'),
      },
      states: {
        emptyTitle: t('crm.dealsList.states.emptyTitle'),
        emptySearchDescription: t('crm.dealsList.states.emptySearchDescription'),
        emptyCreateTitle: t('crm.dealsList.states.emptyCreateTitle'),
        emptyCreateDescription: t('crm.dealsList.states.emptyCreateDescription'),
      },
      confirmations: {
        deleteSelectedTitle: t('crm.dealsList.confirmations.deleteSelectedTitle'),
        deleteSelectedMessage: (count: number, noun: string) =>
          t('crm.dealsList.confirmations.deleteSelectedMessage', {
            values: { count, noun },
          }),
      },
      messages: {
        deleted: (count: number, noun: string) =>
          t('crm.dealsList.messages.deleted', {
            values: { count, noun },
          }),
        restored: (count: number, noun: string) =>
          t('crm.dealsList.messages.restored', {
            values: { count, noun },
          }),
        deleteFailed: (count: number, noun: string) =>
          t('crm.dealsList.messages.deleteFailed', {
            values: { count, noun },
          }),
      },
      errors: {
        failedRestoreDeals: t('crm.dealsList.errors.failedRestoreDeals'),
        errorDeletingDeals: t('crm.dealsList.errors.errorDeletingDeals'),
      },
      details: {
        closeDate: (date: string) =>
          t('crm.dealsList.details.closeDate', {
            values: { date },
          }),
      },
    },
  };
}
